/**
 * Wave avatars (Design System v2.1 / v3.3, "Avatars").
 *
 * Every person gets one of 12 two-tone wave images, picked from their ID so the
 * same person always looks the same. The rule is the spec's: FNV-1a 32-bit hash
 * of the ID (offset 0x811c9dc5, prime 0x01000193), then `hash % 12 + 1`.
 * Example: `usr_mkim` -> wave 05. With no ID the fallback image shows.
 *
 * Pure functions only, so the unit suite (node environment, no SFC plugin) can
 * import them; the images themselves are resolved in PersonAvatar.vue.
 */

export const AVATAR_WAVE_COUNT = 12;

/** The 12 wave numbers, for pickers. */
export const AVATAR_WAVES: readonly number[] = Object.freeze(
    Array.from({ length: AVATAR_WAVE_COUNT }, (_, i) => i + 1),
);

/** FNV-1a 32-bit over the UTF-8 bytes of `input`, as an unsigned integer. */
export function fnv1a32(input: string): number {
    let hash = 0x811c9dc5;
    const bytes = new TextEncoder().encode(input);
    for (let i = 0; i < bytes.length; i++) {
        hash ^= bytes[i];
        hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return hash >>> 0;
}

/**
 * The wave (1-12) for a user ID, or `null` when there is no usable ID yet
 * (the caller then shows the fallback). Numeric IDs are hashed as their
 * decimal string, so `42` and `'42'` give the same wave.
 */
export function avatarWave(userId: string | number | null | undefined): number | null {
    if (userId === null || userId === undefined) return null;
    const id = String(userId).trim();
    if (!id) return null;
    return (fnv1a32(id) % AVATAR_WAVE_COUNT) + 1;
}

/** `5` -> `'05'`, the file name stem of a wave image. */
export function waveFileStem(wave: number): string {
    return String(wave).padStart(2, '0');
}

export const AVATAR_SIZES = {
    xs: 24,
    sm: 32,
    md: 40,
    lg: 48,
    xl: 64,
    '2xl': 96,
} as const;

export type AvatarSize = keyof typeof AVATAR_SIZES;

/** A preset name or a pixel number -> pixels. Unknown values fall back to md. */
export function avatarPx(size: AvatarSize | number | string | undefined): number {
    if (typeof size === 'number' && size > 0) return size;
    if (typeof size === 'string') {
        if (size in AVATAR_SIZES) return AVATAR_SIZES[size as AvatarSize];
        const n = Number(size);
        if (Number.isFinite(n) && n > 0) return n;
    }
    return AVATAR_SIZES.md;
}
